import React, { useMemo, useState } from 'react';
import { X, AlertCircle, CheckCircle, Loader2, ChevronDown } from 'lucide-react';
import { cn } from '../utils/cn';
import { 
  fetchContractABI, 
  validateABI, 
  SUPPORTED_CHAINS, 
  ContractABI, 
  ChainConfig,
  getAvailableFunctions 
} from '../utils/abiFetcher';

interface ProjectSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (projectData: ProjectData) => void;
}

export interface ProjectData {
  name: string;
  contractAddress: string;
  chainId: number;
  abi: ContractABI;
  selectedFunction?: string;
}

enum SetupStep {
  PROJECT_INFO = 'project_info',
  ABI_FETCH = 'abi_fetch',
  FUNCTION_SELECT = 'function_select'
}

const ProjectSetupModal: React.FC<ProjectSetupModalProps> = ({ isOpen, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState<SetupStep>(SetupStep.PROJECT_INFO);
  const [projectName, setProjectName] = useState('');
  const [contractAddress, setContractAddress] = useState('');
  const [selectedChain, setSelectedChain] = useState<ChainConfig>(SUPPORTED_CHAINS[0]); // Sepolia by default
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contractABI, setContractABI] = useState<ContractABI | null>(null);
  const [manualABI, setManualABI] = useState('');
  const [showManualInput, setShowManualInput] = useState(false);
  const [showChainDropdown, setShowChainDropdown] = useState(false);
  const [selectedFunction, setSelectedFunction] = useState<string>('');

  const resetForm = () => {
    setCurrentStep(SetupStep.PROJECT_INFO);
    setProjectName('');
    setContractAddress('');
    setSelectedChain(SUPPORTED_CHAINS[0]);
    setIsLoading(false);
    setError(null);
    setContractABI(null);
    setManualABI('');
    setShowManualInput(false);
    setShowChainDropdown(false);
    setSelectedFunction('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleProjectInfoNext = () => {
    if (!projectName.trim()) {
      setError('Project name is required');
      return;
    }
    
    if (!contractAddress.trim()) {
      setError('Contract address is required');
      return;
    }
    
    if (!/^0x[a-fA-F0-9]{40}$/.test(contractAddress.trim())) {
      setError('Invalid contract address format');
      return;
    }
    
    setError(null);
    setCurrentStep(SetupStep.ABI_FETCH);
  };

  const handleFetchABI = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const abi = await fetchContractABI(contractAddress.trim(), selectedChain.chainId);
      setContractABI(abi);
      // Move to function selection step
      setCurrentStep(SetupStep.FUNCTION_SELECT);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to fetch ABI');
      setShowManualInput(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleManualABI = () => {
    if (!manualABI.trim()) {
      setError('Please enter the contract ABI');
      return;
    }
    
    try {
      const functions = validateABI(manualABI.trim());
      const abi: ContractABI = {
        contractAddress: contractAddress.trim(),
        chainId: selectedChain.chainId,
        abi: functions,
        verified: false
      };
      setContractABI(abi);
      setError(null);
      // Move to function selection step
      setCurrentStep(SetupStep.FUNCTION_SELECT);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Invalid ABI format');
    }
  };

  // Inline ABI validator used from Function Selection empty state
  const handleInlineABIReplace = () => {
    if (!manualABI.trim()) {
      setError('Please paste a contract ABI');
      return;
    }
    try {
      const functions = validateABI(manualABI.trim());
      const abi: ContractABI = {
        contractAddress: contractAddress.trim(),
        chainId: selectedChain.chainId,
        abi: functions,
        verified: false
      };
      setContractABI(abi);
      setSelectedFunction('');
      setError(null);
      // Stay on FUNCTION_SELECT; the list will refresh based on new ABI
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Invalid ABI format');
    }
  };

  const handleComplete = (abi: ContractABI) => {
    if (!selectedFunction) {
      setError('Please select a target function');
      return;
    }
    const projectData: ProjectData = {
      name: projectName.trim(),
      contractAddress: contractAddress.trim(),
      chainId: selectedChain.chainId,
      abi: abi,
      selectedFunction
    };
    
    onComplete(projectData);
    handleClose();
  };

  const availableFunctions = contractABI ? getAvailableFunctions(contractABI) : [];

  // Filter to functions whose first parameter is authData tuple
  const authDataFunctions = useMemo(() => {
    if (!contractABI) return [] as ReturnType<typeof getAvailableFunctions>;
    const expectedTypes = ['uint256','uint256','bytes32','tuple[]','bytes','bool','bytes'];
    return availableFunctions.filter(f => {
      if (!f.inputs || f.inputs.length === 0) return false;
      const first = f.inputs[0] as { name: string; type: string; components?: Array<{ name: string; type: string; components?: Array<{ name: string; type: string }>; }>; };
      if (first.type !== 'tuple' || !('components' in first) || !first.components) return false;
      const actual = first.components.map((c) => c.type);
      if (actual.length !== expectedTypes.length) return false;
      for (let i = 0; i < expectedTypes.length; i++) {
        if (expectedTypes[i] !== actual[i]) return false;
      }
      // verify executions tuple[] component
      const exec = first.components[3] as { type: string; components?: Array<{ type: string }>; } | undefined;
      if (!(exec && exec.type === 'tuple[]' && 'components' in exec && exec.components)) return false;
      const execTypes = exec.components.map((c) => c.type);
      return execTypes.length === 3 && execTypes[0] === 'bytes32' && execTypes[1] === 'bytes' && execTypes[2] === 'bytes';
    });
  }, [contractABI, availableFunctions]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-n8n-dark-800 rounded-n8n border border-n8n-dark-600 shadow-n8n-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-n8n-dark-700">
          <div>
            <h2 className="text-xl font-semibold text-white">Create New Project</h2>
            <p className="text-sm text-n8n-dark-300 mt-1">
              {currentStep === SetupStep.PROJECT_INFO && 'Enter your project details'}
              {currentStep === SetupStep.ABI_FETCH && 'Fetch or paste contract ABI'}
              {currentStep === SetupStep.FUNCTION_SELECT && 'Select target function'}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-n8n-dark-400 hover:text-white transition-colors rounded-n8n hover:bg-n8n-dark-700"
          >
            <X size={20} />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-n8n-dark-700">
          <div className="flex items-center space-x-4">
            {Object.values(SetupStep).map((step, index) => (
              <div key={step} className="flex items-center">
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors",
                  currentStep === step || (index < Object.values(SetupStep).indexOf(currentStep))
                    ? "bg-n8n-primary-500 text-white" 
                    : "bg-n8n-dark-600 text-n8n-dark-300"
                )}>
                  {index + 1}
                </div>
                {index < Object.values(SetupStep).length - 1 && (
                  <div className={cn(
                    "w-12 h-0.5 mx-2 transition-colors",
                    index < Object.values(SetupStep).indexOf(currentStep)
                      ? "bg-n8n-primary-500"
                      : "bg-n8n-dark-600"
                  )} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Project Info */}
          {currentStep === SetupStep.PROJECT_INFO && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-n8n-dark-200 mb-2">
                  Project Name
                </label>
                <input
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  className={cn(
                    "w-full p-3 bg-n8n-dark-700 border border-n8n-dark-600 rounded-n8n",
                    "text-white placeholder-n8n-dark-400 focus:ring-2 focus:ring-n8n-primary-500",
                    "focus:border-n8n-primary-500 transition-all duration-200"
                  )}
                  placeholder="My DApp Project"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-n8n-dark-200 mb-2">
                  Blockchain Network
                </label>
                <div className="relative">
                  <button
                    onClick={() => setShowChainDropdown(!showChainDropdown)}
                    className={cn(
                      "w-full p-3 bg-n8n-dark-700 border border-n8n-dark-600 rounded-n8n",
                      "text-white focus:ring-2 focus:ring-n8n-primary-500 focus:border-n8n-primary-500",
                      "transition-all duration-200 flex items-center justify-between"
                    )}
                  >
                    <span className={selectedChain.enabled ? 'text-white' : 'text-n8n-dark-400'}>
                      {selectedChain.name}
                      {!selectedChain.enabled && ' (Coming Soon)'}
                    </span>
                    <ChevronDown size={16} className="text-n8n-dark-400" />
                  </button>
                  
                  {showChainDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-n8n-dark-700 border border-n8n-dark-600 rounded-n8n shadow-n8n-lg z-10 max-h-60 overflow-y-auto">
                      {SUPPORTED_CHAINS.map((chain) => (
                        <button
                          key={chain.chainId}
                          onClick={() => {
                            if (chain.enabled) {
                              setSelectedChain(chain);
                            }
                            setShowChainDropdown(false);
                          }}
                          disabled={!chain.enabled}
                          className={cn(
                            "w-full p-3 text-left hover:bg-n8n-dark-600 transition-colors",
                            chain.enabled ? 'text-white cursor-pointer' : 'text-n8n-dark-400 cursor-not-allowed',
                            selectedChain.chainId === chain.chainId && 'bg-n8n-dark-600'
                          )}
                        >
                          {chain.name}
                          {!chain.enabled && ' (Coming Soon)'}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-n8n-dark-200 mb-2">
                  Contract Address
                </label>
                <input
                  type="text"
                  value={contractAddress}
                  onChange={(e) => setContractAddress(e.target.value)}
                  className={cn(
                    "w-full p-3 bg-n8n-dark-700 border border-n8n-dark-600 rounded-n8n",
                    "text-white placeholder-n8n-dark-400 focus:ring-2 focus:ring-n8n-primary-500",
                    "focus:border-n8n-primary-500 transition-all duration-200 font-mono"
                  )}
                  placeholder="0x..."
                />
              </div>


              {error && (
                <div className="flex items-center space-x-2 p-3 bg-red-900/20 border border-red-700/50 rounded-n8n">
                  <AlertCircle size={16} className="text-red-400" />
                  <span className="text-red-300 text-sm">{error}</span>
                </div>
              )}

              <button
                onClick={handleProjectInfoNext}
                className={cn(
                  "w-full p-3 bg-n8n-primary-500 hover:bg-n8n-primary-600 text-white rounded-n8n",
                  "transition-colors duration-200 font-medium"
                )}
              >
                Next: Fetch Contract ABI
              </button>
            </div>
          )}

          {/* Step 2: ABI Fetch */}
          {currentStep === SetupStep.ABI_FETCH && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-medium text-white mb-2">Contract ABI</h3>
                <p className="text-n8n-dark-300">
                  We'll try to fetch the ABI automatically from {selectedChain.explorer}
                </p>
              </div>

              <div className="bg-n8n-dark-700 rounded-n8n border border-n8n-dark-600 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-n8n-dark-200">Contract</span>
                  <span className="text-xs text-n8n-dark-400">{selectedChain.name}</span>
                </div>
                <p className="font-mono text-sm text-white break-all">{contractAddress}</p>
              </div>

              {!showManualInput ? (
                <div className="space-y-4">
                  <button
                    onClick={handleFetchABI}
                    disabled={isLoading}
                    className={cn(
                      "w-full p-3 bg-n8n-primary-500 hover:bg-n8n-primary-600 text-white rounded-n8n",
                      "transition-colors duration-200 font-medium flex items-center justify-center space-x-2",
                      isLoading && "opacity-75 cursor-not-allowed"
                    )}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        <span>Fetching ABI...</span>
                      </>
                    ) : (
                      <span>Fetch ABI from Explorer</span>
                    )}
                  </button>

                  <button
                    onClick={() => setShowManualInput(true)}
                    className="w-full p-3 bg-n8n-dark-700 hover:bg-n8n-dark-600 text-n8n-dark-200 border border-n8n-dark-600 rounded-n8n transition-colors duration-200"
                  >
                    Enter ABI Manually
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-n8n-dark-200 mb-2">
                      Contract ABI (JSON)
                    </label>
                    <textarea
                      value={manualABI}
                      onChange={(e) => setManualABI(e.target.value)}
                      className={cn(
                        "w-full p-3 bg-n8n-dark-700 border border-n8n-dark-600 rounded-n8n",
                        "text-white placeholder-n8n-dark-400 focus:ring-2 focus:ring-n8n-primary-500",
                        "focus:border-n8n-primary-500 transition-all duration-200 font-mono text-sm resize-none"
                      )}
                      rows={10}
                      placeholder="Paste your contract ABI here..."
                    />
                  </div>

                  <div className="flex space-x-3">
                    <button
                      onClick={handleManualABI}
                      className="flex-1 p-3 bg-n8n-primary-500 hover:bg-n8n-primary-600 text-white rounded-n8n transition-colors duration-200 font-medium"
                    >
                      Use This ABI
                    </button>
                    <button
                      onClick={() => setShowManualInput(false)}
                      className="flex-1 p-3 bg-n8n-dark-700 hover:bg-n8n-dark-600 text-n8n-dark-200 border border-n8n-dark-600 rounded-n8n transition-colors duration-200"
                    >
                      Back to Auto-Fetch
                    </button>
                  </div>
                </div>
              )}

              {error && (
                <div className="flex items-center space-x-2 p-3 bg-red-900/20 border border-red-700/50 rounded-n8n">
                  <AlertCircle size={16} className="text-red-400" />
                  <span className="text-red-300 text-sm">{error}</span>
                </div>
              )}

              <button
                onClick={() => setCurrentStep(SetupStep.PROJECT_INFO)}
                className="w-full p-3 bg-n8n-dark-700 hover:bg-n8n-dark-600 text-n8n-dark-200 border border-n8n-dark-600 rounded-n8n transition-colors duration-200"
              >
                Back
              </button>
            </div>
          )}

          {/* Step 3: Function Selection */}
          {currentStep === SetupStep.FUNCTION_SELECT && contractABI && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-medium text-white mb-2">Select Target Function</h3>
                <p className="text-n8n-dark-300">Choose a function whose first parameter is authData</p>
              </div>

              <div className="space-y-3">
                {authDataFunctions.length === 0 ? (
                  <div className="p-3 bg-yellow-900/20 border border-yellow-700/50 rounded-n8n text-yellow-300 text-sm space-y-3">
                    <div>
                      No compatible functions were found with <span className="font-mono text-yellow-200">authData</span> tuple.
                    </div>
                    <div className="text-yellow-200">Please try a different contract/ABI or network.</div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setCurrentStep(SetupStep.ABI_FETCH)}
                        className="px-3 py-2 bg-n8n-dark-700 hover:bg-n8n-dark-600 text-yellow-200 border border-yellow-700/50 rounded-md transition-colors"
                      >
                        ← Back to ABI
                      </button>
                      <button
                        onClick={() => setCurrentStep(SetupStep.PROJECT_INFO)}
                        className="px-3 py-2 bg-n8n-dark-700 hover:bg-n8n-dark-600 text-yellow-200 border border-yellow-700/50 rounded-md transition-colors"
                      >
                        Change Network
                      </button>
                    </div>

                    {/* Inline tips */}
                    <div className="mt-2 p-2 bg-n8n-dark-800 border border-yellow-800/40 rounded text-yellow-200">
                      <div className="font-medium mb-1">Expected authData tuple layout</div>
                    </div>

                    {/* Inline paste ABI again */}
                    <div className="mt-2 space-y-2">
                      <label className="block text-xs font-medium text-yellow-200">Paste a different ABI</label>
                      <textarea
                        value={manualABI}
                        onChange={(e) => setManualABI(e.target.value)}
                        className={cn(
                          "w-full p-2 text-xs bg-n8n-dark-800 border border-yellow-800/40 rounded",
                          "text-yellow-100 placeholder-yellow-300/60 focus:ring-2 focus:ring-yellow-600",
                          "focus:border-yellow-600 transition-all duration-200 font-mono"
                        )}
                        rows={6}
                        placeholder="Paste ABI JSON here to try again"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={handleInlineABIReplace}
                          className="px-3 py-2 bg-n8n-primary-600 hover:bg-n8n-primary-700 text-white rounded-md transition-colors"
                        >
                          Use This ABI
                        </button>
                        <button
                          onClick={() => setManualABI('')}
                          className="px-3 py-2 bg-n8n-dark-700 hover:bg-n8n-dark-600 text-yellow-200 border border-yellow-700/50 rounded-md transition-colors"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 bg-n8n-dark-700 border border-n8n-dark-600 rounded-n8n">
                    <label className="block text-xs font-medium text-n8n-primary-400 mb-2">Available Functions</label>
                    <select
                      value={selectedFunction}
                      onChange={(e) => setSelectedFunction(e.target.value)}
                      className={cn(
                        "w-full p-2 text-sm bg-n8n-dark-800 border border-n8n-dark-500 rounded-lg",
                        "text-white focus:ring-2 focus:ring-n8n-primary-500 focus:border-n8n-primary-500",
                        "transition-all duration-200"
                      )}
                    >
                      <option value="">Select a function...</option>
                      {authDataFunctions.map((f, idx) => (
                        <option key={idx} value={f.signature}>
                          {f.name} - {f.signature}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {error && (
                <div className="flex items-center space-x-2 p-3 bg-red-900/20 border border-red-700/50 rounded-n8n">
                  <AlertCircle size={16} className="text-red-400" />
                  <span className="text-red-300 text-sm">{error}</span>
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={() => setCurrentStep(SetupStep.ABI_FETCH)}
                  className="flex-1 p-3 bg-n8n-dark-700 hover:bg-n8n-dark-600 text-n8n-dark-200 border border-n8n-dark-600 rounded-n8n transition-colors duration-200"
                >
                  Back
                </button>
                <button
                  onClick={() => contractABI && handleComplete(contractABI)}
                  disabled={!selectedFunction}
                  className={cn(
                    "flex-1 p-3 bg-n8n-primary-500 hover:bg-n8n-primary-600 text-white rounded-n8n transition-colors duration-200 font-medium",
                    !selectedFunction && "opacity-60 cursor-not-allowed"
                  )}
                >
                  Create Project
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ProjectSetupModal;